import React from 'react'
import { useBooksStore } from '../../store/booksStore'
import './HomeAllBooksDashboard.css'
import './HomeDashboardShared.css'

interface BookCardCompactProps {
    bookId: string
    onClick: () => void
}

const BookCardCompact: React.FC<BookCardCompactProps> = ({ bookId, onClick }) => {
    const { books, getBookChapters, getChapterWitnesses } = useBooksStore()
    const book = books.find(b => b.id === bookId)

    if (!book) return null

    const chapters = getBookChapters(book.id)
    const totalWitnesses = chapters.reduce((total, chapter) => {
        return total + getChapterWitnesses(chapter.id).length
    }, 0)

    const completedWitnesses = chapters.reduce((total, chapter) => {
        const witnesses = getChapterWitnesses(chapter.id)
        return total + witnesses.filter(w => w.preprocessing_result).length
    }, 0)

    const status: 'complete' | 'processing' | 'pending' = (() => {
        if (totalWitnesses > 0 && completedWitnesses === totalWitnesses) return 'complete'
        if (completedWitnesses > 0) return 'processing'
        return 'pending'
    })()

    const progress = totalWitnesses > 0 ? (completedWitnesses / totalWitnesses) * 100 : 0

    // Format timestamp
    const getTimestampText = (): string => {
        const timestamp = book.last_modified || book.created_at
        const now = Date.now()
        const diff = now - timestamp

        if (diff < 60000) return 'just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
        return `${Math.floor(diff / 86400000)}d ago`
    }

    const getStatusText = (): string => {
        if (totalWitnesses === 0) return 'No content'
        if (status === 'complete') return 'All preprocessed'
        if (status === 'processing') return `${completedWitnesses}/${totalWitnesses} ready`
        return 'Pending'
    }

    return (
        <div
            className={`book-card-compact status-${status}`}
            onClick={onClick}
        >
            <div className="card-stripe" />

            <div className="card-content">
                <div className="card-header-row">
                    <div className="book-name">
                        {book.name}
                    </div>
                    <svg
                        className="action-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="19" r="1" />
                    </svg>
                </div>
                {book.description && (
                    <div className="book-description">{book.description}</div>
                )}
            </div>

            <div className="card-progress-track">
                <div
                    className="card-progress-fill"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="card-footer">
                <span>{getStatusText()}</span>
                <span className="meta-counts">
                    {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'} • {getTimestampText()}
                </span>
            </div>
        </div>
    )
}

interface HomeAllBooksDashboardProps {
    onBookClick: (bookId: string) => void
}

const HomeAllBooksDashboard: React.FC<HomeAllBooksDashboardProps> = ({ onBookClick }) => {
    const { books, createBook, getBookChapters, getChapterWitnesses } = useBooksStore()
    const [isCreating, setIsCreating] = React.useState(false)
    const [newBookName, setNewBookName] = React.useState('')
    const [sortBy, setSortBy] = React.useState<'recent' | 'alpha'>('recent')

    // Sort books
    const sortedBooks = [...books].sort((a, b) => {
        if (sortBy === 'alpha') {
            return (a.name || '').localeCompare(b.name || '')
        }
        // Recent first - get max timestamp for each book
        const getBookTimestamp = (book: typeof books[0]) => {
            const chapters = getBookChapters(book.id)
            if (chapters.length === 0) return book.last_modified || book.created_at
            const timestamps = chapters.map(chapter => {
                const witnesses = getChapterWitnesses(chapter.id)
                if (witnesses.length === 0) return chapter.created_at
                return Math.max(chapter.created_at, ...witnesses.map(w => w.last_modified || 0))
            })
            return Math.max(book.last_modified || book.created_at, ...timestamps)
        }
        return getBookTimestamp(b) - getBookTimestamp(a)
    })

    const handleStartCreate = () => {
        setIsCreating(true)
        setNewBookName('')
        // Focus the input after state updates
        setTimeout(() => {
            const input = document.querySelector('.inline-create-input') as HTMLInputElement
            input?.focus()
        }, 0)
    }

    const handleCancelCreate = () => {
        setIsCreating(false)
        setNewBookName('')
    }

    const handleSubmitCreate = async () => {
        if (!newBookName.trim()) return

        try {
            await createBook(newBookName.trim())
            setIsCreating(false)
            setNewBookName('')
        } catch (error) {
            console.error("Failed to create book:", error)
            alert("Failed to create book")
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmitCreate()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelCreate()
        }
    }

    return (
        <div className="home-all-books-dashboard">
            <div className="context-header">
                <div className="context-breadcrumbs">
                    All Books
                </div>
                <div className="context-meta">
                    {books.length} {books.length === 1 ? 'Book' : 'Books'}
                    <span
                        className="sort-toggle"
                        onClick={() => setSortBy(sortBy === 'recent' ? 'alpha' : 'recent')}
                        title={sortBy === 'recent' ? 'Sort alphabetically' : 'Sort by recent'}
                    >
                        {sortBy === 'recent' ? '↓ Recent' : '↓ A-Z'}
                    </span>
                </div>
            </div>

            <div className="books-grid">
                {/* Add New Card or Inline Create Form */}
                {!isCreating ? (
                    <div className="book-card-compact card-add-new" onClick={handleStartCreate}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span className="token-label">Add Book</span>
                    </div>
                ) : (
                    <div className="book-card-compact card-inline-create">
                        <div className="inline-create-form">
                            <input
                                type="text"
                                className="inline-create-input"
                                placeholder="Book name..."
                                value={newBookName}
                                onChange={(e) => setNewBookName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                            />
                            <div className="inline-create-actions">
                                <button
                                    className="inline-create-btn btn-submit"
                                    onClick={handleSubmitCreate}
                                    disabled={!newBookName.trim()}
                                >
                                    Create
                                </button>
                                <button
                                    className="inline-create-btn btn-cancel"
                                    onClick={handleCancelCreate}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {sortedBooks.map((book) => (
                    <BookCardCompact
                        key={book.id}
                        bookId={book.id}
                        onClick={() => onBookClick(book.id)}
                    />
                ))}
            </div>
        </div>
    )
}

export default HomeAllBooksDashboard
